import { Button as AntButton, Modal } from 'antd';
import { FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { checkWebIdDuplicate, updateStudentWebId } from '../../../../api/students.api';
import { IResponse } from '../../../../models/common.api.model';
import useAxios from '../../../../hooks/useAxios';
import useNotification from '../../../../hooks/useNotification';
import ErrorResponse from '../../../ErrorResponse';
import Loader from '../../../Loader';

interface IProps {
  visible: boolean;
  setVisible: (visible: boolean) => void;
  studentCode: string | undefined;
  onSuccess: () => void;
}

const ChangeStudentWebIdModal: FC<IProps> = ({
  visible,
  setVisible,
  studentCode,
  onSuccess,
}) => {
  const { t } = useTranslation();
  const { infoNotification, successNotification } = useNotification();
  const { data: dupRes, isLoading: dupLoading, execute: dupExecute } = useAxios<IResponse<boolean>>();
  const { data: res, isLoading, error, execute } = useAxios();
  const [webId, setWebId] = useState('');
  const [dupChecked, setDupChecked] = useState(false);

  useEffect(() => {
    if (dupRes && !dupLoading) {
      if (dupRes.data === true) {
        setDupChecked(true);
        successNotification(t('사용 가능한 아이디입니다.'));
      } else {
        setDupChecked(false);
        infoNotification(t('이미 사용 중인 아이디입니다.'));
      }
    }
  }, [dupRes, dupLoading]);

  useEffect(() => {
    if (res && !isLoading) {
      successNotification(t('common.message.edit1'));
      onSuccess();
      onClose();
    }
  }, [res, isLoading]);

  const onClose = () => {
    setWebId('');
    setDupChecked(false);
    setVisible(false);
  };

  const onWebIdChange = (value: string) => {
    setWebId(value);
    setDupChecked(false);
  };

  const isSystemIssuedFormat = (value: string) => /^CB\d{8}$/i.test(value);

  const onCheckDuplicate = () => {
    if (!webId) return;
    if (isSystemIssuedFormat(webId)) {
      return infoNotification(t('시스템에서 발급한 형태(CB + 숫자 8자리)는 사용할 수 없습니다.'));
    }
    dupExecute(checkWebIdDuplicate(webId));
  };

  const onSubmit = () => {
    if (!studentCode || !webId) return;
    if (!dupChecked) return infoNotification(t('중복확인을 먼저 해주세요.'));
    if (isSystemIssuedFormat(webId)) {
      return infoNotification(t('시스템에서 발급한 형태(CB + 숫자 8자리)는 사용할 수 없습니다.'));
    }

    execute(updateStudentWebId({ studentCode, webId }));
  };

  return (
    <>
      {(isLoading || dupLoading) ? <Loader /> : null}
      <ErrorResponse error={error} />

      <Modal
        title={t('아이디 변경')}
        visible={visible}
        onCancel={onClose}
        footer={[
          <div className='comm-btn__area justify-content-center'>
            <AntButton key="submit" className='btn-basic pink' type="primary" onClick={onSubmit}>
              {t('common.button.save')}
            </AntButton>
            <AntButton key="cancel" className='btn-basic gray' onClick={onClose}>
              {t('common.button.cancel')}
            </AntButton>
          </div>
        ]}
      >
        <div className="form-group row justify-content-center">
          <div className="col-2 text-right v-line">{t('아이디')}</div>
          <div className="col-5">
            <input
              type="text"
              className="form-control w-100"
              placeholder={t('변경할 아이디를 입력해 주세요')}
              value={webId}
              onChange={(e) => onWebIdChange(e.target.value)}
            />
          </div>
          <div className="col-2">
            <button className="btn-basic small mint" onClick={onCheckDuplicate}>
              {t('중복확인')}
            </button>
          </div>
        </div>
        <div className="mt-3 text-center">
          <div>* {t('학생용 로그인 아이디 변경 기능입니다. (READI, Alex, 수업용)')}</div>
          <div className="text-danger font-weight-bold">
            * {t('변경 후에는 재변경 및 수정이 불가 하오니, 학부모님께 신중히 확인 바랍니다.')}
          </div>
        </div>
      </Modal>
    </>
  );
};

export default ChangeStudentWebIdModal;
